import { indicationOptions } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheet-labels";
import { Box, Typography } from "@material-ui/core";
import type {
  AortaSegmentFindings,
  AortaWorksheetData,
} from "../../../../global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { EPOCUS_TEXT_SECONDARY, rem } from "../epocus-palette";
import {
  AllNormalToggle,
  BooleanToggle,
  IndicationChips,
  NotesField,
  NumericInput,
  ViewChecklist,
  WorksheetColumnLeft,
  WorksheetColumnRight,
  WorksheetColumns,
  WorksheetSection,
} from "./worksheet-primitives";

const INDICATION_OPTIONS = indicationOptions("aorta");

const VIEW_OPTIONS = [
  { key: "proximalTrans", label: "Proximal Trans" },
  { key: "proximalSag", label: "Proximal Sag" },
  { key: "midTrans", label: "Mid Trans" },
  { key: "midSag", label: "Mid Sag" },
  { key: "distalTrans", label: "Distal Trans" },
  { key: "distalSag", label: "Distal Sag" },
] as const;

const SEGMENTS = [
  { key: "proximal", label: "Proximal" },
  { key: "mid", label: "Mid" },
  { key: "distal", label: "Distal" },
] as const;

export const isAortaComplete = (data: AortaWorksheetData): boolean =>
  SEGMENTS.every((s) => {
    const seg = data.findings[s.key];
    return (
      seg.diameterCm !== null &&
      Number.isFinite(seg.diameterCm) &&
      seg.diameterCm >= 0 &&
      seg.aneurysm !== null &&
      seg.dissectionFlap !== null &&
      seg.muralThrombus !== null
    );
  });

const FindingLabel: FC<{ children: string }> = ({ children }) => (
  <Typography
    style={{
      fontSize: rem(13),
      fontWeight: 600,
      color: EPOCUS_TEXT_SECONDARY,
      marginTop: 4,
    }}
  >
    {children}
  </Typography>
);

const SegmentCard: FC<{
  label: string;
  segment: AortaSegmentFindings;
  onChange: (segment: AortaSegmentFindings) => void;
  disabled: boolean;
}> = ({ label, segment, onChange, disabled }) => (
  <WorksheetSection label={label}>
    <NumericInput
      value={segment.diameterCm}
      onChange={(v) => onChange({ ...segment, diameterCm: v })}
      unit="cm"
      step={0.1}
      label="Diameter"
      disabled={disabled}
    />
    <FindingLabel>Aneurysm</FindingLabel>
    <BooleanToggle
      value={segment.aneurysm}
      onChange={(v) => onChange({ ...segment, aneurysm: v })}
      disabled={disabled}
    />
    <FindingLabel>Dissection Flap</FindingLabel>
    <BooleanToggle
      value={segment.dissectionFlap}
      onChange={(v) => onChange({ ...segment, dissectionFlap: v })}
      disabled={disabled}
    />
    <FindingLabel>Mural Thrombus</FindingLabel>
    <BooleanToggle
      value={segment.muralThrombus}
      onChange={(v) => onChange({ ...segment, muralThrombus: v })}
      disabled={disabled}
    />
  </WorksheetSection>
);

export const AortaWorksheet: FC<{
  data: AortaWorksheetData;
  onChange: (data: AortaWorksheetData) => void;
  isAllNormal: boolean;
  onToggleAllNormal: () => void;
  readOnly: boolean;
  imageGallery?: React.ReactNode;
}> = ({
  data,
  onChange,
  isAllNormal,
  onToggleAllNormal,
  readOnly,
  imageGallery,
}) => {
  const updateSegment = (
    key: keyof AortaWorksheetData["findings"],
    segment: AortaSegmentFindings,
  ) => {
    onChange({
      ...data,
      findings: { ...data.findings, [key]: segment },
    });
  };

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <AllNormalToggle
        isAllNormal={isAllNormal}
        onToggle={onToggleAllNormal}
        disabled={readOnly}
      />

      <WorksheetColumns>
        <WorksheetColumnLeft>
          <WorksheetSection label="Indication">
            <IndicationChips
              options={[...INDICATION_OPTIONS]}
              selected={data.indication}
              onChange={(v) =>
                onChange({
                  ...data,
                  indication: v as AortaWorksheetData["indication"],
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>

          <WorksheetSection label="Views">
            <Typography
              style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
            >
              Tap to toggle obtained status
            </Typography>
            <ViewChecklist
              views={[...VIEW_OPTIONS]}
              checked={data.views}
              onChange={(key, checked) =>
                onChange({
                  ...data,
                  views: { ...data.views, [key]: checked },
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>
        </WorksheetColumnLeft>

        <WorksheetColumnRight>
          {SEGMENTS.map((s) => (
            <SegmentCard
              key={s.key}
              label={s.label}
              segment={data.findings[s.key]}
              onChange={(seg) => updateSegment(s.key, seg)}
              disabled={readOnly}
            />
          ))}

          {imageGallery && (
            <WorksheetSection label="Images / Clips">
              {imageGallery}
            </WorksheetSection>
          )}

          <WorksheetSection label="Notes">
            <NotesField
              value={data.notes}
              onChange={(v) => onChange({ ...data, notes: v })}
              disabled={readOnly}
            />
          </WorksheetSection>
        </WorksheetColumnRight>
      </WorksheetColumns>
    </Box>
  );
};
